import React, { useRef, useState } from 'react';
import { Sparkles, Feather, Mic, Loader2, Check, Play, Pause } from 'lucide-react';
import BookShell from './BookShell';

/* ── Sent2ment palette ── */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
  border: 'rgba(198, 169, 107, 0.25)',
};

/* The buyer writes only on these four pages. Every other page is held quietly
   for the recipient. Titles are intentionally short and emotionally precise. */
const HIDDEN_PROMPTS = {
  1: 'The Beginning',
  7: 'A gentle moment',
  18: 'A quiet reminder',
  30: 'When you need it most',
};
const HIDDEN_PAGES = new Set(Object.keys(HIDDEN_PROMPTS).map((n) => parseInt(n, 10)));

/* The 26 read-only recipient pages keep their gentle one-line prompts so the
   recipient has a soft suggestion when their time comes — purely visual, not
   editable by the buyer. */
const RECIPIENT_PROMPTS = {
  2: 'Something I love about myself',
  3: 'A memory I want to hold onto',
  4: 'A song that finds me on hard days',
  5: 'Something that makes me feel safe',
  6: 'A small thing that made me smile this week',
  8: 'A place I’d like to return to',
  9: 'Something I’ve never said out loud',
  10: 'The version of me I’m becoming',
  11: 'A lesson I’m still learning',
  12: 'Something I want to carry forward',
  13: 'A moment I would live again',
  14: 'The first time I felt understood',
  15: 'A quiet wish I’m holding',
  16: 'Three small things I needed to hear today',
  17: 'Something I’d like to forgive myself for',
  19: 'Something soft — just because',
  20: 'A truth I never want to doubt',
  21: 'A thank-you I never quite said',
  22: 'How I see my future, today',
  23: 'A line I’d write to my younger self',
  24: 'What “home” feels like to me',
  25: 'Something I’ve learned from being loved',
  26: 'A laugh I want to remember',
  27: 'Something to read on a missing-them day',
  28: 'A promise I’m keeping for myself',
  29: 'The kind of love I hope to grow into',
};

const LOCKED_PLACEHOLDER =
  "Kept blank, just for them. They'll write here in their own time \u2014 whenever the moment feels right.";

/* ── In-page audio control — appears at the top of page 1 (The Beginning).
   Lets the buyer add/replace a voice note INSIDE the journal book itself,
   instead of as a separate boxed section above the book. Reuses the same
   handleAudioUpload flow on the parent (slot="extra") so storage logic is
   completely unchanged. ── */
const InPageAudio = ({ audioUrl, onUpload, busy }) => {
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePick = () => { if (!busy) inputRef.current?.click(); };
  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) onUpload(file);
  };
  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };

  return (
    <div
      className="mb-5 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3"
      style={{
        background: 'rgba(255, 251, 234, 0.7)',
        border: `1px dashed ${C.border}`,
        borderRadius: '6px',
      }}
      data-testid="journal-page-1-audio"
    >
      <button
        type="button"
        onClick={handlePick}
        disabled={busy}
        data-testid="journal-page-1-audio-pick"
        className="inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-50"
        style={{
          background: 'transparent',
          color: C.gold,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          fontSize: '12.5px',
          letterSpacing: '0.04em',
        }}
      >
        {busy ? (
          <><Loader2 size={13} className="animate-spin" /> Uploading…</>
        ) : audioUrl ? (
          <><Check size={13} /> Voice added — tap to replace</>
        ) : (
          <><Mic size={13} strokeWidth={1.6} /> Add your voice (optional)</>
        )}
      </button>

      {audioUrl && (
        <button
          type="button"
          onClick={togglePlay}
          data-testid="journal-page-1-audio-play"
          aria-label={playing ? 'Pause' : 'Listen'}
          className="inline-flex items-center gap-2 px-4 py-1.5 transition-all duration-300 hover:opacity-90"
          style={{
            background: 'rgba(198, 169, 107, 0.18)',
            border: `1px solid ${C.gold}`,
            borderRadius: '999px',
            color: C.midnight,
            fontFamily: 'Georgia, serif',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.08em',
          }}
        >
          {playing ? <Pause size={12} strokeWidth={1.6} /> : <Play size={12} strokeWidth={1.6} />}
          {playing ? 'Pause' : 'Listen'}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.oga,.opus,.webm,.amr,.3gp,.3gpp,.flac"
        onChange={handleChange}
        className="hidden"
        data-testid="journal-page-1-audio-input"
      />
      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="sr-only"
      />
    </div>
  );
};

/* ── Sealed (read-only) Listen pill — same calm visual language as the
   recipient's ListenPill so a finalised vault never falls back to raw
   browser audio controls. ── */
const SealedListenPill = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };
  return (
    <div className="mb-5 flex flex-col items-start" data-testid="journal-page-1-sealed-listen">
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause message' : 'Listen to your message'}
        className="inline-flex items-center justify-center gap-2.5 px-5 py-3 transition-all duration-300 hover:opacity-90"
        style={{
          background: `linear-gradient(180deg, ${C.gold}28 0%, ${C.gold}3F 100%)`,
          border: `1.25px solid ${C.gold}`,
          borderRadius: '999px',
          color: C.midnight,
          letterSpacing: '0.08em',
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 ${C.gold}33, 0 6px 14px rgba(30, 45, 69, 0.16)`,
        }}
      >
        {playing ? <Pause size={13} strokeWidth={1.6} /> : <Play size={13} strokeWidth={1.6} style={{ marginLeft: '1px' }} />}
        {playing ? 'Pause' : 'Listen to your message'}
      </button>
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="sr-only"
      />
    </div>
  );
};

/* ── Hidden-message page (editable by the buyer) ─────────────────── */
const HiddenPage = ({ pageNumber, prompt, value, onChange, audio, locked }) => (
  <div
    data-testid={`journal-page-${pageNumber}`}
    data-editable="true"
    className="h-full flex flex-col"
  >
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <p
        className="text-[10px] tracking-[0.35em] uppercase font-semibold"
        style={{ color: C.gold }}
        data-testid={`journal-page-${pageNumber}-number`}
      >
        Page {pageNumber}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase italic"
        style={{ color: C.gold, fontFamily: 'Georgia, serif' }}
        data-testid={`journal-page-${pageNumber}-hidden-badge`}
      >
        <Sparkles size={11} strokeWidth={1.4} /> Hidden moment &middot; you write
      </span>
    </div>

    <h3
      className="text-2xl sm:text-[1.6rem] font-light leading-snug mb-3"
      style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
      data-testid={`journal-page-${pageNumber}-prompt`}
    >
      {prompt}
    </h3>

    <p
      className="text-sm italic mb-5 leading-relaxed max-w-prose"
      style={{
        color: 'rgba(11, 31, 58, 0.72)',
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.01em',
      }}
      data-testid={`journal-page-${pageNumber}-hidden-note`}
    >
      This page will be gently revealed to them at the right moment. Anything you
      write here is held with extra care.
    </p>

    {audio && !locked && (
      <InPageAudio
        audioUrl={audio.audioUrl}
        onUpload={audio.onUpload}
        busy={audio.busy}
      />
    )}

    {/* Sealed read-only voice — calm styled "Listen" pill (no native controls)
        so the sealed page still feels like a premium product, not a flat
        browser audio strip. */}
    {audio && locked && audio.audioUrl && (
      <SealedListenPill src={audio.audioUrl} />
    )}

    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Take your time. Write only what feels right&hellip;"
      rows={8}
      maxLength={5000}
      readOnly={locked}
      disabled={locked}
      data-testid={`journal-page-${pageNumber}-textarea`}
      className="w-full px-4 py-3 text-[15px] outline-none transition-colors duration-300 resize-none flex-1 disabled:opacity-90"
      style={{
        background: '#FCF7E6',
        color: C.text,
        border: `1px solid ${C.border}`,
        borderRadius: '6px',
        fontFamily: 'Georgia, serif',
        lineHeight: '1.7',
        minHeight: '180px',
        cursor: locked ? 'default' : 'text',
      }}
    />
  </div>
);

/* ── Recipient-only page (NOT editable) ─────────────────────────── */
const LockedPage = ({ pageNumber, prompt }) => (
  <div
    data-testid={`journal-page-${pageNumber}`}
    data-editable="false"
    aria-readonly="true"
    className="h-full flex flex-col"
  >
    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
      <p
        className="text-[10px] tracking-[0.35em] uppercase font-semibold"
        style={{ color: `${C.gold}AA` }}
        data-testid={`journal-page-${pageNumber}-number`}
      >
        Page {pageNumber}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase italic"
        style={{ color: `${C.textSec}AA`, fontFamily: 'Georgia, serif' }}
        data-testid={`journal-page-${pageNumber}-recipient-badge`}
      >
        <Feather size={11} strokeWidth={1.4} /> For them to fill in
      </span>
    </div>

    <h3
      className="text-lg sm:text-xl font-light leading-snug mb-4"
      style={{ fontFamily: "'Cormorant Garamond', serif", color: `${C.midnight}D9`, fontStyle: 'italic' }}
      data-testid={`journal-page-${pageNumber}-prompt`}
    >
      {prompt}
    </h3>

    {/* Static box — no input element, no cursor, no possibility of typing. */}
    <div
      className="px-4 py-5 select-none flex-1 flex items-center justify-center"
      style={{
        background: 'rgba(255, 251, 234, 0.55)',
        border: `1px dashed ${C.border}`,
        borderRadius: '6px',
        cursor: 'default',
        minHeight: '180px',
      }}
      data-testid={`journal-page-${pageNumber}-recipient-placeholder`}
    >
      <p
        className="text-[13px] italic leading-relaxed text-center"
        style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
      >
        {LOCKED_PLACEHOLDER}
      </p>
    </div>
  </div>
);

/* ── Section ──────────────────────────────────────────────────── */
const InteractiveJournalSection = ({
  values = {},
  onChangePage = () => {},
  audioUrl,
  onAudioUpload,
  audioBusy,
  locked = false,
}) => {
  const renderPage = (pageNumber) =>
    HIDDEN_PAGES.has(pageNumber) ? (
      <HiddenPage
        pageNumber={pageNumber}
        prompt={HIDDEN_PROMPTS[pageNumber]}
        value={values[pageNumber]}
        onChange={(next) => onChangePage(pageNumber, next)}
        audio={
          pageNumber === 1
            ? { audioUrl, onUpload: onAudioUpload, busy: !!audioBusy }
            : null
        }
        locked={locked}
      />
    ) : (
      <LockedPage pageNumber={pageNumber} prompt={RECIPIENT_PROMPTS[pageNumber]} />
    );

  return (
    <BookShell
      totalPages={30}
      renderPage={renderPage}
      header={{
        overline: 'Interactive Journal · 30 quiet pages',
        title: 'Four messages from you. Thirty pages, gently held.',
        body:
          "Four messages you’ve written — gently placed within pages they can return to, and slowly make their own.",
        footnote: (
          <>
            <span
              style={{
                fontStyle: 'normal',
                fontWeight: 600,
                color: '#0B1F3A',
                letterSpacing: '0.02em',
              }}
            >
              Pages 1, 7, 18 and 30
            </span>
            {' '}are yours to write now. The remaining pages are kept quietly blank, ready for them.
          </>
        ),
      }}
      sectionTestId="vault-edit-section-interactive-journal"
      brandTestId="interactive-journal-brand-mark"
      coverTestId="interactive-journal-cover"
    />
  );
};

export default InteractiveJournalSection;
